<template>
  <div class="color-scheme-switcher flex items-center justify-center ml-2">
    <button
      class="color-scheme-switcher__value"
      @click.stop="toggleDropdown"
    >
      <component :is="getCurrentIcon" />
    </button>
    
    <Teleport to="body">
      <div v-show="isDropdownVisible" 
           class="color-scheme-switcher__dropdown"
           :style="{
             position: 'fixed',
             top: dropdownPosition.top + 'px',
             left: dropdownPosition.left + 'px'
           }">
        <button
          class="color-scheme-switcher__option"
          @click="setUserScheme('auto')"
        >
          <Auto />
          <span class="color-scheme-switcher__text">Auto</span>
        </button>
        
        <button
          class="color-scheme-switcher__option"
          @click="setUserScheme('light')"
        >
          <Sun />
          <span class="color-scheme-switcher__text">Light</span>
        </button>
        <button
          class="color-scheme-switcher__option"
          @click="setUserScheme('dark')"
        >
          <Moon />
          <span class="color-scheme-switcher__text">Dark</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import { applyScheme, getSavedScheme, getSystemScheme, removeSavedScheme } from '@/utils/colorSchemeUtils';
import Auto from "@/components/Icons/AutoIcon.vue";
import Moon from "@/components/Icons/MoonIcon.vue";
import Sun from "@/components/Icons/SunIcon.vue";

const userScheme = ref(getSavedScheme() || 'auto');
const isDropdownVisible = ref(false);
const dropdownPosition = ref({ top: 0, left: 0 });

const updateDropdownPosition = () => {
  const button = document.querySelector('.color-scheme-switcher__value');
  if (button) {
    const rect = button.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom + 10,
      left: rect.left - 100
    };
  }
};

const applyUserScheme = () => {
  if (userScheme.value === 'auto') {
    removeSavedScheme();
    applyScheme(getSystemScheme());
  } else {
    applyScheme(userScheme.value, true);
  }
};

const setUserScheme = (scheme) => {
  userScheme.value = scheme;
  isDropdownVisible.value = false;
};

const toggleDropdown = () => {
  isDropdownVisible.value = !isDropdownVisible.value;
  if (isDropdownVisible.value) {
    updateDropdownPosition();
  }
};

const closeDropdownOnOutsideClick = (event) => {
  if (!event.target.closest('.color-scheme-switcher__dropdown')) {
    isDropdownVisible.value = false;
  }
};

const closeDropdown = () => {
  isDropdownVisible.value = false;
}

const getCurrentIcon = computed(() => {
  if (userScheme.value === 'auto') return Auto;
  if (userScheme.value === 'dark') return Moon;
  if (userScheme.value === 'light') return Sun;
});

watch(userScheme, applyUserScheme, { immediate: true });

onMounted(() => {
  document.addEventListener('click', closeDropdownOnOutsideClick);
  window.addEventListener('resize', updateDropdownPosition);
  window.addEventListener('scroll', updateDropdownPosition);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdownOnOutsideClick);
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition);
});
</script>

<style scoped>
.color-scheme-switcher {
  position: relative;
  z-index: var(--z-index-popover);
}
.color-scheme-switcher__value {
  border-radius: 50%;
  background: var(--background-primary);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-scheme-switcher-button);
  transition: .2s ease-in;
  &:hover {
    color: var(--icon-hover);
  }
}

.color-scheme-switcher__option {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: var(--text-01);
  cursor: pointer;
  width: 100%;
  padding: 0 12px;
  box-sizing: border-box;
}

.color-scheme-switcher__text {
  font-size: 14px;
  line-height: 16px;
  margin-left: 12px;
}

.color-scheme-switcher__option:hover {
  background: var(--ui-03);
}

.color-scheme-switcher__dropdown {
  background: var(--ui-01);
  border: 1px solid var(--ui-03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
  border-radius: 8px;
  padding: 6px 0;
  z-index: var(--z-index-popover);
  width: 124px;
}
</style>
